import { FormProvider } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { FormInput } from '@microservices/wiskey-react-components'
import { Box, Grid, Typography } from '@mui/material'

import { PageContentLayout } from '@layouts/PageContentLayout'
import { EditConfigMenu, useEditConfigMenu } from '@components/ConfigEditMenu'
import { EditConfigScriptDialog } from '@components/EditConfigScriptDialog'
import { GeneralButtons } from '@components/GeneralButtons'

import { EditConfigProvider } from '@providers/EditConfigProvider'

import {
  ENTITY_CONFIG_TYPE,
  GENERATOR_INPUT_TYPE,
  REGEX,
  VALUE_TYPE_OPTIONS,
  VARIABLE_TYPE_OPTIONS,
} from '@constants'

import { useVariables } from '../../hooks/useVariables'

export const VariableCreateOrEdit = () => {
  const { t } = useTranslation()

  const {
    configMenuOption,
    handleSelectConfigMenuOption,
    configFileText,
    handleApply,
    handleFileUpload,
    isOpenScriptDialog,
    setIsOpenScriptDialog,
    handleCancel: handleCancelEditConfig,
    handleCopyScript,
  } = useEditConfigMenu(ENTITY_CONFIG_TYPE.VARIABLES)

  const {
    data: { methods, isDirty, isEdit, isLoadingVariablesById, isCreateOrUpdateLoading, variableId },
    handlers: { handleSave, handleCancel },
  } = useVariables()

  const modelType = isEdit ? 'edit' : 'create'

  return (
    <PageContentLayout>
      <EditConfigProvider entityType={ENTITY_CONFIG_TYPE.VARIABLES}>
        <EditConfigScriptDialog />
        <Box sx={{ width: '100%' }}>
          <form>
            <FormProvider {...methods}>
              <Box p={1} pb={0}>
                <Grid
                  container
                  alignItems={'center'}
                  justifyContent={'space-between'}
                  pb={2}
                  py={0.5}
                >
                  <Typography variant='h5'>{t(`variables.title.${modelType}`)}</Typography>
                  {variableId && <EditConfigMenu configId={variableId} />}
                </Grid>
                <FormInput
                  disabled={modelType === 'edit'}
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  label={t(`variables.label.internalId`)}
                  labelSx={{ width: 150 }}
                  loading={isLoadingVariablesById}
                  name='internalId'
                  placeholder={t(`variables.label.internalId`)}
                  replacePattern={REGEX.MODEL_CODE_REPLACE_PATTERN}
                  size='small'
                  sx={{ pt: 1 }}
                  rules={{
                    required: true,
                    validate: value => value.trim().length !== 0,
                  }}
                />
                <FormInput
                  inputType={GENERATOR_INPUT_TYPE.INPUT}
                  label={t(`variables.label.title`)}
                  labelSx={{ width: 150 }}
                  loading={isLoadingVariablesById}
                  name='title'
                  placeholder={t(`variables.label.title`)}
                  size='small'
                  rules={{
                    required: true,
                    validate: value => value.trim().length !== 0,
                  }}
                />
                <FormInput
                  autocompleteOptions={VARIABLE_TYPE_OPTIONS}
                  inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                  label={t(`variables.label.variableType`)}
                  labelSx={{ width: 150 }}
                  loading={isLoadingVariablesById}
                  name='variableType'
                  placeholder={t(`variables.label.variableType`)}
                  rules={{ required: true }}
                  size='small'
                />
                <FormInput
                  autocompleteOptions={VALUE_TYPE_OPTIONS}
                  inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
                  label={t(`variables.label.valueType`)}
                  labelSx={{ width: 150 }}
                  loading={isLoadingVariablesById}
                  name='valueType'
                  placeholder={t(`variables.label.valueType`)}
                  rules={{ required: true }}
                  size='small'
                />
              </Box>
              <Grid
                container
                display='flex'
                flexDirection='row'
                justifyContent='flex-end'
                p='15px 5px'
              >
                <GeneralButtons
                  cancelText={t('variables.cancelButton')}
                  isLoading={isCreateOrUpdateLoading || isLoadingVariablesById}
                  saveDisabled={!isDirty || isCreateOrUpdateLoading || isLoadingVariablesById}
                  saveText={t('variables.saveButton')}
                  onCancel={handleCancel}
                  onSave={handleSave}
                />
              </Grid>
            </FormProvider>
          </form>
        </Box>
      </EditConfigProvider>
    </PageContentLayout>
  )
}
